import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CscbQuestionInfo from './CscbQuestionInfo';
import CscbAccordion from './CscbAccordion';
import assessmentReader from '../../../../store/readHelpers/AssessmentReader';
import assessmentActions from '../../../../actions/AssessmentActions';
import styles from './cscb.scss';
import objectTypes from '../../../../constants/ObjectTypes';
import { reportError } from './../../../../services/ErrorReporting';

function Cscb({ currentlyShown, questionSelection }) {
    const [sectionData, setSectionData] = useState(null);
    const [preparedQuestions, setPreparedQuestions] = useState(new Set());

    useEffect(() => {
        processSectionsData();
    }, [currentlyShown]);

    const getStimulusQuestion = (inlineSection, question) => {
        if (!inlineSection || !question) return null;

        if (inlineSection?.initialStimulus?.parentId === question?.parentId) return inlineSection?.initialStimulus;

        const selectedSection = inlineSection.sections?.find(
            section => section.id === question.parentId
        );

        if (!selectedSection) return null;

        const stimulusQuestion = inlineSection.questions?.find(
            ques => ques.parentId === selectedSection.id && ques.category === objectTypes.CATEGORY.STIMULUS
        );

        return stimulusQuestion || null;
    };

    const safelyPrepareQuestions = (questions, processed) => {
        const newPreparedQuestions = new Set();
        if (questions && questions.length > 0) {
            for (const question of questions) {
                try {
                    // Only prepare if question hasn't been parsed yet
                    if (!question.isParsed) {
                        assessmentActions.prepareQuestion(question);
                    }
                    newPreparedQuestions.add(question.contentRevisionId);

                    // Also prepare stimulus if exists
                    const stimulusQuestion = getStimulusQuestion(processed, question);
                    if (stimulusQuestion && !stimulusQuestion.isParsed) {
                        assessmentActions.prepareQuestion(stimulusQuestion, true);
                    }
                } catch (error) {
                    reportError(error);
                }
            }
        }
        return newPreparedQuestions;
    };

    const processSectionsData = () => {
        const sections = assessmentReader.getSections();
        const sectionId = currentlyShown?.sectionId;

        if (sectionId) {
            const processed = sections.find(section => section?.id === +sectionId);
            if (processed) {
                const questions = processed?.questions?.filter(question => question?.category !== objectTypes.CATEGORY.STIMULUS);
                const newPreparedQuestions = safelyPrepareQuestions(questions, processed);

                setPreparedQuestions(newPreparedQuestions);
                setSectionData({
                    parentId: processed.parentId,
                    sectionId: processed.id,
                    sectionTitle: processed.title,
                    questionPairs: questions?.map((ques) => {
                        return {
                            ...ques,
                            isSelected: questionSelection[ques.contentRevisionId],
                            stimulusQuestion: getStimulusQuestion(processed, ques),
                        };
                    }),
                    sections: processed.sections,
                });
            }
        }
    };

    const renderAccordion = (question, index) => (
        <CscbAccordion
            key={question.contentRevisionId}
            number={index + 1}
            questionPairs={sectionData.questionPairs}
            sectionId={sectionData.sectionId}
            currQuestion={question}
            areAllQuestionsPrepared={sectionData.questionPairs?.every(qst => preparedQuestions.has(qst.contentRevisionId))}
        />
    );

    return (
        <React.Fragment>
            <CscbQuestionInfo
                sectionId={currentlyShown?.sectionId}
                currentInlineSection={sectionData}
            />
            <div className={styles.cscbAppContainer}>
                {sectionData?.questionPairs?.map((question, sectionIndex) =>
                    renderAccordion(question, sectionIndex)
                )}
            </div>
        </React.Fragment>
    );
}

Cscb.propTypes = {
    currentlyShown: PropTypes.object.isRequired,
    questionSelection: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
    currentlyShown: state.currentlyShown,
    response: state.response,
    questionSelection: state.questionSelection,
});

export default connect(mapStateToProps)(Cscb);
